import * as React from 'react';
import {
  StackLayout,
  Button,
  Modal,
  HeadingText,
  ParagraphText,
  TextInput,
  UtilityText,
  FormField,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';

export interface MoveToPositionModalProps {
  modalDescription: string;
  onMove: (targetIndex: number) => void;
  currentIndex: number;
  arrayLength: number;
  itemLabel: string;
  children: React.ReactNode;
}

/**
 * Modal that allows users to move an array item to a specific position by entering the target index.
 */
export const MoveToPositionModal: React.FC<
  React.PropsWithChildren<MoveToPositionModalProps>
> = ({
  children,
  onMove,
  modalDescription,
  currentIndex,
  arrayLength,
  itemLabel,
}) => {
  const { formatMessage } = useIntl();

  const [modalOpen, setModalOpen] = React.useState(false);
  const [targetPosition, setTargetPosition] = React.useState('');
  const [error, setError] = React.useState('');

  const onApplyMove = React.useCallback(() => {
    const targetIndex = parseInt(targetPosition, 10);

    // Validate input
    if (Number.isNaN(targetIndex)) {
      setError(formatMessage({ id: 'INVALID_POSITION_ERROR' }));
      return;
    }

    if (targetIndex < 1 || targetIndex > arrayLength) {
      setError(
        formatMessage(
          { id: 'POSITION_OUT_OF_RANGE_ERROR' },
          { max: arrayLength },
        ),
      );
      return;
    }

    if (targetIndex === currentIndex + 1) {
      setError(formatMessage({ id: 'SAME_POSITION_ERROR' }));
      return;
    }

    // Convert to 0-based index and call onMove
    onMove(targetIndex - 1);
    setModalOpen(false);
    setTargetPosition('');
    setError('');
  }, [onMove, targetPosition, currentIndex, arrayLength, formatMessage]);

  const onModalOpenChange = React.useCallback(() => {
    setModalOpen((previous) => !previous);
    if (!modalOpen) {
      setTargetPosition('');
      setError('');
    }
  }, [modalOpen]);

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setTargetPosition(event.target.value);
      setError(''); // Clear error when user starts typing
    },
    [],
  );

  return (
    <Modal.Root open={modalOpen} onOpenChange={onModalOpenChange}>
      <Modal.Trigger>{children}</Modal.Trigger>
      <Modal.Content showCloseButton={false}>
        <Modal.Title>
          <HeadingText level="2" size="lg">
            {formatMessage({ id: 'MOVE_TO_POSITION_MODAL_TITLE' })}
          </HeadingText>
        </Modal.Title>
        <Modal.Description>
          <StackLayout spacing="$threeQuarters">
            <ParagraphText>{modalDescription}</ParagraphText>
            <StackLayout spacing="$one">
              <UtilityText>
                {formatMessage(
                  { id: 'CURRENT_POSITION_INFO' },
                  {
                    item: `"${itemLabel}"`,
                    currentPosition: currentIndex + 1,
                    totalItems: arrayLength,
                  },
                )}
              </UtilityText>
              <FormField
                id="target-position"
                label={formatMessage({ id: 'TARGET_POSITION_LABEL' })}
                inputStatus={error ? 'error' : undefined}
                statusMessage={error}
              >
                <TextInput
                  id="target-position"
                  value={targetPosition}
                  onChange={handleInputChange}
                  placeholder={formatMessage(
                    { id: 'TARGET_POSITION_PLACEHOLDER' },
                    { min: 1, max: arrayLength },
                  )}
                  type="number"
                  min={1}
                  max={arrayLength}
                />
              </FormField>
            </StackLayout>
          </StackLayout>
        </Modal.Description>
        <StackLayout
          orientation="horizontal"
          horizontalAlignment="end"
          spacing="$half"
          css={{ marginBlockStart: '$one' }}
        >
          <Modal.Close>
            <Button priority="tertiary" quiet size="medium" type="button">
              {formatMessage({ id: 'CANCEL' })}
            </Button>
          </Modal.Close>
          <Button
            size="medium"
            onClick={onApplyMove}
            disabled={!targetPosition.trim()}
          >
            {formatMessage({ id: 'MOVE' })}
          </Button>
        </StackLayout>
      </Modal.Content>
    </Modal.Root>
  );
};
